// 地球のリズム：季節（四季と土用）と潮回り（大潮・中潮…）の目安（端末内計算）
import { SearchSunLongitude, SunPosition } from 'astronomy-engine';

export type Season = '春' | '夏' | '秋' | '冬';

export type EarthInfo = {
  season: Season;            // 暦の上の季節（立春・立夏・立秋・立冬で切り替え）
  inDoyo: boolean;           // 土用の期間中か
  doyoSeason: Season | null; // どの季節の土用か（入っていれば）
  doyoStart: Date | null;    // 今の、または次の土用の入り
  doyoEnd: Date | null;      // 今の、または次の土用の明け（次の「立」）
  daysToDoyo: number | null; // 次の土用の入りまで（期間中は null）
  tide: TideName;            // 潮回りの目安
};

export type TideName = '大潮' | '中潮' | '小潮' | '長潮' | '若潮';

const DAY_MS = 86400000;
function sod(d: Date) { return new Date(d.getFullYear(), d.getMonth(), d.getDate()); }
function daysBetween(a: Date, b: Date) { return Math.round((sod(b).getTime() - sod(a).getTime()) / DAY_MS); }

/** 太陽黄経から暦の季節。立春315°・立夏45°・立秋135°・立冬225° */
function seasonOf(elon: number): Season {
  if (elon >= 315 || elon < 45) return '春';
  if (elon < 135) return '夏';
  if (elon < 225) return '秋';
  return '冬';
}

// 土用の入り（太陽黄経）と、その土用が属する季節。各「立」の18日ほど前
const DOYO: { lon: number; season: Season; endLon: number }[] = [
  { lon: 297, season: '冬', endLon: 315 }, // 冬の土用 → 立春
  { lon: 27, season: '春', endLon: 45 },   // 春の土用 → 立夏
  { lon: 117, season: '夏', endLon: 135 }, // 夏の土用 → 立秋
  { lon: 207, season: '秋', endLon: 225 }, // 秋の土用 → 立冬
];

function inRange(elon: number, from: number, to: number) {
  return from < to ? elon >= from && elon < to : elon >= from || elon < to;
}

/**
 * 潮回り（旧暦日ベースの一般的な目安）。月齢から旧暦日を近似する。
 * 1〜3・14〜17・30 大潮／4〜6・12〜13・18〜20・26〜29 中潮／7〜9・21〜23 小潮／10・24 長潮／11・25 若潮
 */
export function tideFromMoonAge(age: number): TideName {
  const day = Math.min(30, Math.floor(age) + 1);
  if (day <= 3 || (day >= 14 && day <= 17) || day === 30) return '大潮';
  if (day <= 6 || (day >= 12 && day <= 13) || (day >= 18 && day <= 20) || day >= 26) return '中潮';
  if (day <= 9 || (day >= 21 && day <= 23)) return '小潮';
  if (day === 10 || day === 24) return '長潮';
  return '若潮';
}

export function getEarthInfo(now: Date, moonAge: number): EarthInfo {
  const elon = SunPosition(now).elon;
  const season = seasonOf(elon);

  const cur = DOYO.find((d) => inRange(elon, d.lon, d.endLon)) || null;
  let doyoStart: Date | null = null, doyoEnd: Date | null = null, daysToDoyo: number | null = null;
  if (cur) {
    doyoStart = SearchSunLongitude(cur.lon, new Date(now.getTime() - 25 * DAY_MS), 25)?.date || null;
    doyoEnd = SearchSunLongitude(cur.endLon, now, 25)?.date || null;
  } else {
    // 次の土用
    let best: { d: Date; end: Date | null } | null = null;
    for (const d of DOYO) {
      const t = SearchSunLongitude(d.lon, now, 100);
      if (t && (!best || t.date < best.d)) best = { d: t.date, end: SearchSunLongitude(d.endLon, t.date, 25)?.date || null };
    }
    if (best) { doyoStart = best.d; doyoEnd = best.end; daysToDoyo = daysBetween(now, best.d); }
  }

  return {
    season,
    inDoyo: !!cur,
    doyoSeason: cur ? cur.season : null,
    doyoStart, doyoEnd, daysToDoyo,
    tide: tideFromMoonAge(moonAge),
  };
}
